import { findIndex, merge, omit } from 'lodash';

import { increment, decrement, updatedCounters } from '../services/counters';
import { SEARCH_QUERY_SEND, SEARCH_CLEAR_RESULTS, SEARCH } from '../actions/search';
import { FETCH_SEARCH_RESULTS } from '../actions/search-socket';
import { FETCH_SEARCH_SUCCESS, FETCH_SEARCH_FAILURE } from '../actions/fetch-search';
import { SUBSCRIBE_REQUEST, SUBSCRIBE_FAILURE } from '../actions/subscribe';
import { UNSUBSCRIBE_REQUEST, UNSUBSCRIBE_FAILURE } from '../actions/unsubscribe';
import { INCREMENT_FOLLOWING_COUNT, DECREMENT_FOLLOWING_COUNT } from '../actions/increment-following-count';
import { UPDATE_USER_SUCCESS } from '../actions/update-user';
import { PROMOTE_MODERATOR_SUCCESS } from '../actions/promote-moderator';
import { DEMOTE_MODERATOR_SUCCESS } from '../actions/demote-moderator';
import {
  INCREMENT_POST_LIKE_COUNT_SUCCESS,
  INCREMENT_POST_LIKE_COUNT_FAILURE,
  INCREMENT_POST_LIKE_COUNT_REQUEST,
} from '../actions/increment-post-like-count';
import {
  INCREMENT_COMMENT_LIKE_COUNT_SUCCESS,
  INCREMENT_COMMENT_LIKE_COUNT_FAILURE,
  INCREMENT_COMMENT_LIKE_COUNT_REQUEST,
} from '../actions/increment-comment-like-count';
import { MOVE_POST_SUCCESS } from '../actions/move-post';
import { PIN_POST_SUCCESS } from '../actions/pin-post';
import { UNPIN_POST_SUCCESS } from '../actions/unpin-post';
import { ENABLE_POST_COMMENTS_SUCCESS } from '../actions/enable-post-comments';
import { DISABLE_POST_COMMENTS_SUCCESS } from '../actions/disable-post-comments';
import { DELETE_POST_SUCCESS } from '../actions/delete-post';
import { DELETE_COMMENT_SUCCESS } from '../actions/delete-comment';
import { USER_SET_PERMISSIONS } from '../actions/user-socket';
import { BLOCK_USER_SUCCESS } from '../actions/block-user';
import { UNBLOCK_USER_SUCCESS } from '../actions/unblock-user';

const initialState = {
  lastSearchUuid: undefined,
  posts: [],
  members: [],
};

// eslint-disable-next-line complexity
const post = (state, action) => {
  switch (action.type) {
    case MOVE_POST_SUCCESS:
    case PIN_POST_SUCCESS:
    case UNPIN_POST_SUCCESS:
    case ENABLE_POST_COMMENTS_SUCCESS:
    case DISABLE_POST_COMMENTS_SUCCESS:
      if (state._id === action.payload._id) {
        return { ...action.payload, type: 'post' };
      }
      return state;
    case INCREMENT_POST_LIKE_COUNT_SUCCESS:
    case INCREMENT_COMMENT_LIKE_COUNT_SUCCESS:
      if (state._id === action.payload._id) {
        return omit({ ...state, ...action.payload }, 'isLikeInProgress');
      }
      return state;
    case INCREMENT_COMMENT_LIKE_COUNT_REQUEST:
    case INCREMENT_POST_LIKE_COUNT_REQUEST: {
      if (state._id === action.payload._id) {
        return {
          ...state,
          isLiked: !state.isLiked,
          likeCount: state.likeCount + (state.isLiked ? -1 : 1),
          isLikeInProgress: true,
        };
      }
      return state;
    }
    case INCREMENT_COMMENT_LIKE_COUNT_FAILURE:
    case INCREMENT_POST_LIKE_COUNT_FAILURE:
      if (state._id === action.payload._id) {
        return omit(
          {
            ...state,
            isLiked: !state.isLiked,
            likeCount: state.likeCount + (state.isLiked ? -1 : 1),
          },
          'isLikeInProgress'
        );
      }
      return state;
    case PROMOTE_MODERATOR_SUCCESS:
    case DEMOTE_MODERATOR_SUCCESS:
    case USER_SET_PERMISSIONS:
    case UPDATE_USER_SUCCESS:
    case BLOCK_USER_SUCCESS:
    case UNBLOCK_USER_SUCCESS:
      if (state.owner._id === action.payload._id) {
        return { ...state, owner: action.payload };
      }
      return state;
    default:
      return state;
  }
};

const updateFollowingCount = ({ user, userId, isFollowing }) => {
  if (user._id !== userId) {
    return user;
  }

  return merge({}, user, {
    ...updatedCounters(user, 'followingCount', isFollowing ? increment : decrement),
  });
};

const updateSubscription = ({ user, userId, isSubscribed }) => {
  if (user._id !== userId) {
    return user;
  }

  return merge({}, user, {
    ...updatedCounters(user, 'followerCount', isSubscribed ? increment : decrement),
    isSubscribed,
  });
};

const member = (user, action) => {
  switch (action.type) {
    case PROMOTE_MODERATOR_SUCCESS:
    case DEMOTE_MODERATOR_SUCCESS:
    case USER_SET_PERMISSIONS:
    case UPDATE_USER_SUCCESS:
    case BLOCK_USER_SUCCESS:
    case UNBLOCK_USER_SUCCESS:
      if (user._id === action.payload._id) {
        return action.payload;
      }
      return user;
    case INCREMENT_FOLLOWING_COUNT:
      return updateFollowingCount({ user, userId: action.payload, isFollowing: true });
    case DECREMENT_FOLLOWING_COUNT:
      return updateFollowingCount({ user, userId: action.payload, isFollowing: false });
    case SUBSCRIBE_REQUEST:
      return updateSubscription({ user, userId: action.payload._id, isSubscribed: true });
    case UNSUBSCRIBE_REQUEST:
      return updateSubscription({ user, userId: action.payload._id, isSubscribed: false });
    case SUBSCRIBE_FAILURE:
      return updateSubscription({ user, userId: action.payload._id, isSubscribed: false });
    case UNSUBSCRIBE_FAILURE:
      return updateSubscription({ user, userId: action.payload._id, isSubscribed: true });
    default:
      return user;
  }
};

const renameUsersKey = data => {
  const { users, ...result } = data;
  result.members = users;

  return result;
};

// eslint-disable-next-line complexity
export default (state = initialState, action) => {
  switch (action.type) {
    case SEARCH:
      return { ...state, query: action.payload };
    case SEARCH_QUERY_SEND:
      return { ...state, lastSearchUuid: action.payload.uuid };
    case FETCH_SEARCH_SUCCESS:
      return { ...state, ...renameUsersKey(action.payload), posts: action.payload.posts };
    case FETCH_SEARCH_FAILURE:
    case SEARCH_CLEAR_RESULTS:
      return { ...state, posts: [], members: [] };
    case FETCH_SEARCH_RESULTS:
      if (state.lastSearchUuid === action.payload.uuid) {
        return { ...state, ...renameUsersKey(action.payload.results) };
      }
      return state;
    case SUBSCRIBE_REQUEST:
    case UNSUBSCRIBE_REQUEST:
    case SUBSCRIBE_FAILURE:
    case UNSUBSCRIBE_FAILURE: {
      const index = findIndex(state.members, ['_id', action.payload._id]);
      if (index > -1) {
        const currentMember = member(state.members[index], action);
        return {
          ...state,
          members: [...state.members.slice(0, index), currentMember, ...state.members.slice(index + 1)],
        };
      }
      return state;
    }
    case INCREMENT_FOLLOWING_COUNT:
    case DECREMENT_FOLLOWING_COUNT:
    case PROMOTE_MODERATOR_SUCCESS:
    case DEMOTE_MODERATOR_SUCCESS:
    case USER_SET_PERMISSIONS:
    case UPDATE_USER_SUCCESS:
    case BLOCK_USER_SUCCESS:
    case UNBLOCK_USER_SUCCESS:
      return {
        ...state,
        posts: state.posts.map(entity => post(entity, action)),
        members: state.members.map(entity => member(entity, action)),
      };
    case DELETE_POST_SUCCESS:
    case DELETE_COMMENT_SUCCESS:
      return { ...state, posts: state.posts.filter(post => post._id !== action.payload) };
    case MOVE_POST_SUCCESS:
    case PIN_POST_SUCCESS:
    case UNPIN_POST_SUCCESS:
    case ENABLE_POST_COMMENTS_SUCCESS:
    case DISABLE_POST_COMMENTS_SUCCESS:
    case INCREMENT_POST_LIKE_COUNT_REQUEST:
    case INCREMENT_COMMENT_LIKE_COUNT_REQUEST:
    case INCREMENT_COMMENT_LIKE_COUNT_FAILURE:
    case INCREMENT_COMMENT_LIKE_COUNT_SUCCESS:
    case INCREMENT_POST_LIKE_COUNT_FAILURE:
    case INCREMENT_POST_LIKE_COUNT_SUCCESS: {
      const index = findIndex(state.posts, ['_id', action.payload._id]);
      if (index > -1) {
        const currentPost = post(state.posts[index], action);
        return {
          ...state,
          posts: [...state.posts.slice(0, index), currentPost, ...state.posts.slice(index + 1)],
        };
      }
      return state;
    }
    default:
      return state;
  }
};



// WEBPACK FOOTER //
// ./app/reducers/search.js